import { type ReactElement } from 'react'
import { isDefined, isNotEmpty, type DashboardDataWithEditable, type DashboardId } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { Collapse, Group, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { useTranslation, type HustleDashboards } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { InheritedHustleRow } from './InheritedHustleRow'
import { HustleCard } from './HustleCard'

const PRECEDENCE_INFO =
  "Home shows one Hustle widget — the most specific one that reaches you: your own, then your team's, then your role's, then your company's. The rest are marked with a crossed-out eye."

type Props = {
  hustle: HustleDashboards
  isCreating: boolean
  copyingDashboardId: DashboardId | undefined
  deletion: {
    deletingDashboardId?: DashboardId
    isRemoving: boolean
    onDelete: (dashboard: DashboardDataWithEditable) => void
  }
  onOpen: (dashboard: DashboardDataWithEditable) => void
  onCreate: () => void
  onCopy: (dashboard: DashboardDataWithEditable) => void
  onDuplicate: (dashboard: DashboardDataWithEditable) => void
}

/** The Home section: the pinned hustle card, then anything handed down. */
export function HustleSection({
  hustle,
  isCreating,
  copyingDashboardId,
  deletion,
  onOpen,
  onCreate,
  onCopy,
  onDuplicate,
}: Props): ReactElement {
  const { t } = useTranslation()
  const [isExpanded, { toggle }] = useDisclosure(true)

  return (
    <Stack gap='sm'>
      <Group gap='xs'>
        <Text className='font-semibold' size='lg'>
          {t('Home')}
        </Text>
        {isNotEmpty(hustle.inherited) && (
          <Tooltip multiline label={t(PRECEDENCE_INFO)} w={320}>
            <MaterialSymbolIcon icon='info' size='md' />
          </Tooltip>
        )}
      </Group>

      <HustleCard
        deleting={deletion.isRemoving && deletion.deletingDashboardId === hustle.own?.dashboardId}
        inherited={hustle.inherited[0]}
        isCreating={isCreating}
        own={hustle.own}
        onCreate={onCreate}
        onDelete={deletion.onDelete}
        onDuplicate={onDuplicate}
        onOpen={onOpen}
      />

      {isNotEmpty(hustle.inherited) && (
        <Stack gap='xs'>
          <UnstyledButton onClick={toggle}>
            <Group gap='xs'>
              <MaterialSymbolIcon icon={isExpanded ? 'expand_more' : 'chevron_right'} size='md' />
              <Text c='dimmed' size='sm'>
                {t('Other Hustle Widgets')}
              </Text>
            </Group>
          </UnstyledButton>
          <Collapse in={isExpanded}>
            <Stack gap='xs'>
              {hustle.inherited.map((dashboard) => (
                <InheritedHustleRow
                  dashboard={dashboard}
                  deleting={deletion.isRemoving && deletion.deletingDashboardId === dashboard.dashboardId}
                  isCopying={copyingDashboardId === dashboard.dashboardId}
                  isSurfaced={
                    isDefined(hustle.surfacedDashboardId) && hustle.surfacedDashboardId === dashboard.dashboardId
                  }
                  key={dashboard.dashboardId}
                  onCopy={onCopy}
                  onDelete={deletion.onDelete}
                />
              ))}
            </Stack>
          </Collapse>
        </Stack>
      )}
    </Stack>
  )
}
